import { Resource, ResourceSharingUserModel, ResourceStatus, ResourceType } from "@app/resources";
import { querystringify } from "@app/shared/utility/querystringify";
import ServiceBase from "@app/shared/serviceBase";
import ResourceSharingModel from "@app/resources/resourceSharingModel";
import ResourceUpdateModel from "@app/resources/resourceUpdateModel";

/** Handles communication across the wire when retrieving and interacting with resources */
export default class ResourceService extends ServiceBase {
  /**
   * Updates the status of the resource
   * @param resourceId
   * @param newStatus
   */
  public async updateResourceArchiveStatus(resourceId: number, newStatus: ResourceStatus): Promise<string> {
    return this.makePut<string>(`resources/${resourceId}/updateResourceArchiveStatus/?newStatus=${newStatus}`);
  }

  public async bulkArchive(resourceIds: number[]): Promise<number[]> {
    return await this.makePut<number[]>(`resources/bulkArchive`, resourceIds);
  }

  /**
   * Updates the hidden status of the resource
   * @param resourceId
   * @param newStatus
   */
  public async updateResourceHiddenStatus(resourceId: number): Promise<string> {
    return this.makePut<string>(`resources/${resourceId}/updateResourceHiddenStatus`);
  }

  public async updateResource(resourceId: number, updateModel: ResourceUpdateModel): Promise<string> {
    return this.makePut<string>(`resources/${resourceId}`, updateModel);
  }

  /**
   * Gets all resources of the specified type for the current user
   * @param resourceType
   */
  public async getResourcesByType(resourceType: ResourceType, includeShared = true, includeHidden = false, resourceStatus: ResourceStatus = ResourceStatus.Active): Promise<Resource[]> {
    return this.makeGet<Resource[]>(`resources/type/${resourceType}?includeShared=${includeShared}&includeHidden=${includeHidden}&resourceStatus=${resourceStatus}`);
  }

  /**
   * Gets a list of users that the resource can be shared with, including a status of if the resource was shared.
   * This is specifically for when a resource is being cloned.
   * The endpoint for normal resource sharing may not be the same. If it is we can rename this to cover both bases.
   */
  public async getCloneShareUsers(resourceId: number): Promise<ResourceSharingUserModel[]> {
    return this.makeGet<ResourceSharingUserModel[]>(`resources/${resourceId}/cloneresourcesharingusers`);
  }

  public async shareResource(resourceId: number, userIds: number[]): Promise<void> {
    return this.makePost(`resources/${resourceId}/share`, userIds);
  }

  public async getSharingMatrixData(resourceTypes: ResourceType[]): Promise<ResourceSharingModel[]> {
    const query = {
      resourceTypes: resourceTypes
    };

    return this.makeGet(`resources/resourcesharingmatrix/multiple?${querystringify(query)}`);
  }

  public async getShareResourceData(resourceId: number): Promise<ResourceSharingModel[]> {
    return this.makeGet(`resources/${resourceId}/resourcesharingdata`);
  }

  public async bulkShare(sharedResources: ResourceSharingModel[]): Promise<void> {
    if (!sharedResources || sharedResources.length === 0) {
      return;
    }

    return this.makePost("resources/bulkshare", sharedResources);
  }

  public async getProjectNames(): Promise<string[]> {
    return await this.makeGet<string[]>("resources/projectnames");
  }

  public async resourceNameExists(resourceTypes: ResourceType[], name: string): Promise<boolean> {
    const query = {
      resourceTypes
    };

    return await this.makeGet<boolean>(`resources/exists/${name}?${querystringify(query)}`);
  }

  public async getResourceType(resourceId: number): Promise<ResourceType> {
    return await this.makeGet(`resources/${resourceId}/type`);
  }
}
